import React, { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { UsageLimitExceededError } from "@/hooks/useUsageLimits";

interface UsageLimitContextType {
  currentError: UsageLimitExceededError | null;
  showLimitError: (error: UsageLimitExceededError) => void;
  clearLimitError: () => void;
  showPricingDialog: boolean;
  setShowPricingDialog: (show: boolean) => void;
}

const UsageLimitContext = createContext<UsageLimitContextType | null>(null);

export function UsageLimitProvider({ children }: { children: ReactNode }) {
  const [currentError, setCurrentError] = useState<UsageLimitExceededError | null>(null);
  const [showPricingDialog, setShowPricingDialog] = useState(false);

  const showLimitError = useCallback((error: UsageLimitExceededError) => {
    setCurrentError(error);
    // Auto-clear after 10 seconds
    setTimeout(() => {
      setCurrentError((prev) => (prev === error ? null : prev));
    }, 10000);
  }, []);

  const clearLimitError = useCallback(() => {
    setCurrentError(null);
  }, []);

  return (
    <UsageLimitContext.Provider
      value={{
        currentError,
        showLimitError,
        clearLimitError,
        showPricingDialog,
        setShowPricingDialog,
      }}
    >
      {children}
    </UsageLimitContext.Provider>
  );
}

export function useUsageLimitContext() {
  const context = useContext(UsageLimitContext);
  if (!context) {
    throw new Error("useUsageLimitContext must be used within a UsageLimitProvider");
  }
  return context;
}
